import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="mb-4">
      <button
        onClick={onClick}
        className={`w-full p-6 flex justify-between items-center text-left transition-all duration-500 rounded-[2rem] border ${isOpen
            ? 'bg-white border-[#a6192e] shadow-xl shadow-red-50 z-10'
            : 'bg-gray-50 border-gray-100 hover:border-gray-200 hover:bg-white'
          } group relative`}
      >
        <span className={`text-sm md:text-base font-black italic uppercase tracking-tight transition-colors duration-300 ${isOpen ? 'text-[#a6192e]' : 'text-slate-700 group-hover:text-slate-900'
          }`}>
          {question}
        </span>

        <motion.div
          animate={{ rotate: isOpen ? 180 : 0, color: isOpen ? '#a6192e' : '#94a3b8' }}
          transition={{ duration: 0.4 }}
          className="flex-shrink-0 ml-4"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" />
          </svg>
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0, y: -20 }}
            animate={{ height: 'auto', opacity: 1, y: 0 }}
            exit={{ height: 0, opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
            className="overflow-hidden"
          >
            <div className="p-8 text-slate-500 text-sm leading-relaxed italic border-x border-b border-gray-100 rounded-b-[2.5rem] -mt-8 pt-14 bg-white shadow-inner">
              <div className="w-12 h-[3px] bg-[#a6192e] mb-5 opacity-40 rounded-full" />
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQSection = ({ faqs, whatsappNumber }) => {
  const [activeIndex, setActiveIndex] = useState(null);

  if (!faqs || faqs.length === 0) return null;

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <section className="py-24 px-6 max-w-4xl mx-auto flex flex-col items-center">
      <div className="text-center mb-16 space-y-4">
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-[#a6192e] font-black text-[10px] tracking-[0.5em] uppercase italic"
        >
          Soporte Técnico & Ayuda
        </motion.span>
        <h2 className="text-5xl md:text-7xl font-black italic tracking-tighter uppercase leading-none text-slate-900">
          Preguntas <span className="text-[#a6192e]">Frecuentes</span>
        </h2>
      </div>

      <div className="space-y-1 w-full mb-16">
        {faqs.map((faq, index) => (
          <FAQItem
            key={index}
            question={faq.question}
            answer={faq.answer}
            isOpen={activeIndex === index}
            onClick={() => toggleFAQ(index)}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        className="text-center bg-gray-50 p-10 md:p-14 rounded-[3.5rem] border border-gray-100 w-full max-w-2xl shadow-inner relative overflow-hidden"
      >
        <div className="absolute -right-10 -bottom-10 w-32 h-32 bg-[#a6192e]/5 rounded-full blur-3xl" />

        <h4 className="text-slate-900 font-black text-xl italic uppercase tracking-tight mb-3 relative z-10">
          ¿Aún tienes dudas sobre nuestros productos?
        </h4>
        <p className="text-slate-400 text-[10px] italic mb-10 uppercase tracking-[0.2em] font-bold relative z-10">
          Nuestro asesor de ventas te atenderá personalmente.
        </p>

        <a
          href={`https://wa.me/${whatsappNumber?.replace(/\D/g, '')}?text=${encodeURIComponent("Hola, me gustaría recibir más información sobre sus productos.")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="relative z-10 inline-flex items-center gap-4 px-12 py-5 bg-[#a6192e] text-white rounded-full font-black text-xs tracking-[0.2em] hover:scale-105 active:scale-95 transition-all shadow-2xl shadow-red-200 uppercase"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.148-.67-1.614-.917-2.214-.242-.584-.487-.504-.67-.514-.173-.009-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
          </svg>
          Chatear con un asesor
        </a>
      </motion.div>
    </section>
  );
};

export default FAQSection;